/**
 * Destinatario newsletter di un lead/cliente: email generica dell'anagrafica
 * oppure un singolo referente.
 *
 * Modello (nessuna colonna nuova): clients.newsletter_subscribed dice se l'anagrafica
 * riceve la newsletter, clients.newsletter_email l'indirizzo a cui arriva (vuoto =
 * email principale). Il motore delle liste (resolve_newsletter_list_recipients) usa
 * gia' COALESCE(newsletter_email, email) per le tipologie Lead e Clienti, quindi la
 * scelta vale con tutti i filtri e una sola email per anagrafica.
 * Le spunte newsletter dei referenti restano il loro consenso personale (tipologia
 * Referenti) e questa scelta non le tocca.
 */

export const NEWSLETTER_COMPANY = 'company';
export const NEWSLETTER_OTHER = 'other';
const REF_PREFIX = 'ref:';

export interface NewsletterRecipientOption {
  value: string;
  label: string;
  description?: string;
  email: string;
}

const norm = (v?: string | null) => (v || '').trim().toLowerCase();

/** Referenti attivi con email, senza doppioni per indirizzo. */
function referentiWithEmail(contacts: any[] | null | undefined): { name: string; email: string }[] {
  const seen = new Set<string>();
  const out: { name: string; email: string }[] = [];
  for (const c of contacts || []) {
    if (c?.is_active === false) continue;
    const email = (c?.email || '').trim();
    if (!email || seen.has(norm(email))) continue;
    seen.add(norm(email));
    const name = (c.name || `${c.first_name || ''} ${c.last_name || ''}`).trim() || email;
    out.push({ name, email });
  }
  return out;
}

/** Scelte disponibili: email generica dell'anagrafica + un'opzione per referente con email. */
export function newsletterRecipientOptions(
  client: { email?: string | null; entity_type?: string | null },
  contacts: any[] | null | undefined,
): NewsletterRecipientOption[] {
  const options: NewsletterRecipientOption[] = [];
  const mainEmail = (client.email || '').trim();
  if (mainEmail) {
    options.push({
      value: NEWSLETTER_COMPANY,
      label: client.entity_type === 'individual' ? 'Email principale' : 'Email aziendale',
      description: mainEmail,
      email: mainEmail,
    });
  }
  for (const r of referentiWithEmail(contacts)) {
    if (norm(r.email) === norm(mainEmail)) continue;
    options.push({ value: REF_PREFIX + norm(r.email), label: r.name, description: `Referente · ${r.email}`, email: r.email });
  }
  return options;
}

/**
 * Scelta attuale: null = non iscritto; 'company' = email principale; 'ref:<email>' = referente;
 * 'other' = indirizzo che non corrisponde a nessuno dei due (es. inserito a mano nella vecchia scheda).
 */
export function currentNewsletterRecipient(
  client: { newsletter_subscribed?: boolean | null; newsletter_email?: string | null; email?: string | null },
  contacts: any[] | null | undefined,
): string | null {
  if (!client.newsletter_subscribed) return null;
  const target = norm(client.newsletter_email) || norm(client.email);
  if (!target || target === norm(client.email)) return NEWSLETTER_COMPANY;
  if (referentiWithEmail(contacts).some((r) => norm(r.email) === target)) return REF_PREFIX + target;
  return NEWSLETTER_OTHER;
}

/** Campi da salvare sull'anagrafica per una scelta (null = non iscritto). */
export function newsletterRecipientUpdates(
  value: string | null,
  options: NewsletterRecipientOption[],
): { newsletter_subscribed: boolean; newsletter_email?: string | null } {
  if (!value) return { newsletter_subscribed: false };
  // Email aziendale: newsletter_email vuoto, cosi' segue l'email principale se cambia
  if (value === NEWSLETTER_COMPANY) return { newsletter_subscribed: true, newsletter_email: null };
  const opt = options.find((o) => o.value === value);
  return { newsletter_subscribed: true, newsletter_email: opt?.email || null };
}
